/**
 * The supplier contact log, as a detail-panel section.
 *
 * ONE component for both parents. Stacie asked to see "contacted by/date/method" on
 * the hotel panel (item 19) and on an agreement (items 3 and 21), and those are the
 * same list of the same rows — so a second copy would drift the moment either grew
 * a column. The caller says which parent it is; nothing else differs.
 *
 * Fetches its own data rather than taking it from the parent's payload, which is the
 * opposite of how `AgreementRollup` works and the difference is deliberate. The
 * rollup is eager because it is bounded (one query for a whole page of properties)
 * and always wanted. A contact log is unbounded per parent, only read when a row is
 * actually expanded, and — once the form lands — has to refetch after a write. So it
 * owns a request.
 *
 * That means it also owns three states, and says which it is in. "No contacts" and
 * "the request failed" render identically if you let them, and the first is a
 * finding while the second is a bug — the mistake the agreements rollup was
 * corrected for on 2026-08-09.
 */
import { useCallback, useEffect, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';
import { fmtInstant } from '@/features/crm/lib/dates';
import { contactReasonLabel } from '../contact-reasons';
import {
  LogContactForm,
  type LinkableAgreement,
  type SelectableContact,
} from './LogContactForm';

/** One logged contact, as GET /api/btp/contacts returns it. */
export interface SupplierContact {
  id: string;
  contactedAt: string;
  /** The PRIMARY method. Kept while the column is — see `methods`. */
  method: string;
  /** Every method used for this one outreach. */
  methods?: string[];
  /** An agreement-status token, or 'general'. Null on rows logged before it existed. */
  reason?: string | null;
  /** Who at the property was spoken to. Empty is normal. */
  participants?: { id: string; name: string; role: string | null }[];
  /**
   * The agreement this contact was about — set on a HOTEL-level read, null on an
   * agreement-level one where it would restate the parent.
   *
   * This is what makes the hotel's history DIFFERENTIATED rather than a flat
   * list, which is the whole of Stacie's worked example: "if Tyler calls Little
   * America to talk about loading the agreement and he sees that I just talked
   * to Blake yesterday, he could be like — hey Blake, sorry, I know Stacie
   * called, this is something different."
   */
  agreement?: {
    id: string;
    customerId: string | null;
    customerName: string | null;
  } | null;
  direction: string;
  contactedBy: string;
  notes: string | null;
}

/** The derived summary the API computes — see lib/hsp/supplier-contact. */
export interface LastContact {
  at: string;
  method: string;
  direction: string;
  by: string;
  total: number;
  awaitingReply: number;
}

interface ContactsResponse {
  contacts: SupplierContact[];
  total: number;
  last_contact: LastContact | null;
  truncated: boolean;
  /**
   * Machine-written contacts the route withheld — the e2e supplier journey logs
   * one against a real staging property on every run, and Stacie found ~150 of
   * them stacked on one hotel.
   *
   * Reported rather than assumed so the hiding is visible. Optional because a
   * deployed API predating the field simply omits it, and a missing count must
   * read as "none withheld" rather than as an error.
   */
  test_contacts_hidden?: number;
}

/**
 * Human labels for the stored vocabulary. Falls back to the raw value rather than
 * blanking it: a method we have not labelled yet is still information, and 'other'
 * exists precisely because the list is provisional.
 */
const METHOD_LABELS: Record<string, string> = {
  email: 'Email',
  phone: 'Phone',
  portal: 'Portal',
  in_person: 'In person',
  other: 'Other',
};

const DIRECTION_LABELS: Record<string, string> = {
  outbound: 'We contacted them',
  inbound: 'They contacted us',
};

function label(map: Record<string, string>, value: string): string {
  return map[value] ?? value;
}

/**
 * Every method on a row, as "Phone, Email".
 *
 * Falls back to the singular `method` so a row written before `methods` existed
 * still renders — the column is still populated (expand-then-contract), so this
 * is not defensive padding, it is reading the field that is actually there.
 */
function methodsOf(c: SupplierContact): string {
  const list = c.methods?.length ? c.methods : [c.method];
  return list.map((m) => label(METHOD_LABELS, m)).join(', ');
}

/** Local-midnight day key, for grouping "same day" without a timezone bug. */
function dayKeyOf(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toDateString();
}

export interface SupplierContactLogProps {
  /**
   * At least one. Both is allowed by the API as of migration 20260903190000,
   * though no caller needs it yet — a panel is about one thing or the other.
   */
  agreementId?: string;
  hotelCanonicalId?: string;
  /**
   * Called after a contact is SAVED, so the host can refresh what it derives
   * from the contact history.
   *
   * WHY THE HOST AND NOT JUST THIS PANEL. Logging a contact changes the
   * agreement's denormalised `last_contact_{at,by,method}` columns, which the
   * Agreements row renders as "Last contacted by" / "Last contacted date" and
   * which "Our owner" also derives from. Reloading only this panel's own list
   * left all of that stale until a page refresh — Jeff, 2026-09-09: "In today's
   * version, you have to refresh the page to get the updates to show."
   *
   * A REFETCH, not an optimistic patch, and that is forced rather than lazy:
   * `POST /api/btp/contacts` updates those columns CONDITIONALLY — only where
   * `lastContactAt` is null or older than the new contact — so a backdated call
   * legitimately does not move them. The form has a `when` field, so
   * backdating is an everyday action, and a client that patched the row itself
   * would show a last-contact the server rejected.
   */
  onContactSaved?: () => void;
  /**
   * Forwarded to the form's reason picker. Passed through rather than fetched
   * here for the same reason the form does not fetch it: the hotel panel
   * already has the property's agreement rollup.
   */
  agreements?: LinkableAgreement[];
  /**
   * The property whose PEOPLE can be named on a contact — distinct from
   * `hotelCanonicalId`, which selects whose LOG this is.
   *
   * They are the same value on the hotel panel and different on the agreement
   * panel, where the log is per-agreement but the people are still the
   * property's. Two props because they answer two questions; collapsing them
   * would make an agreement-level log start reading the hotel's whole history.
   *
   * FETCHED HERE rather than passed in, which is a correction. The first
   * version took a `contacts` prop, reasoning that `HotelContacts` sits beside
   * this form and already holds the list — but that list lives INSIDE
   * `HotelContacts`, not in either parent, so no caller could actually supply
   * it. Lifting it through two unrelated parents to save one request for a
   * handful of rows is the wrong trade; a sibling fetch of the same bounded
   * endpoint is not.
   */
  propertyCanonicalId?: string;
  testId?: string;
}

export function SupplierContactLog({
  agreementId,
  agreements,
  hotelCanonicalId,
  onContactSaved,
  propertyCanonicalId,
  testId = 'supplier-contact-log',
}: SupplierContactLogProps) {
  const [data, setData] = useState<ContactsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  /**
   * Newest first by default, because "what happened most recently" is the
   * question this panel is opened to answer. Ascending is one click away, which
   * is the ask: "what if we could just even like click to sort the column."
   *
   * Note this is the OPPOSITE default from the Loading tab's own last-contact
   * sort, and deliberately: that one is a to-do list, so the least recently
   * touched has to surface first. This is a history.
   */
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  /** The property's people, for the form's "who did you speak to" picker. */
  const [contacts, setContacts] = useState<SelectableContact[]>([]);

  // The query string is derived once and used as the effect's dependency, so the
  // effect cannot re-run on a re-render that changed nothing.
  const query = agreementId
    ? `agreement_id=${encodeURIComponent(agreementId)}`
    : hotelCanonicalId
      ? `hotel_canonical_id=${encodeURIComponent(hotelCanonicalId)}`
      : '';

  const load = useCallback(() => {
    if (!query) return () => {};
    let cancelled = false;
    setLoading(true);
    setError(null);
    apiFetch<ContactsResponse>(`/api/btp/contacts?${query}`)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) {
          // Kept as an ERROR rather than falling back to an empty list. An empty
          // list here reads as "nobody has contacted this hotel", which is a claim
          // about the hotel; a failed request is a claim about us.
          setError(e instanceof ApiError ? e.message : String(e));
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [query]);

  useEffect(() => load(), [load]);

  /*
   * The pickable people, loaded separately and FAILING SOFT.
   *
   * A failure here empties the picker and nothing else: the contact log is
   * still readable and a call can still be logged without naming anyone, so an
   * error banner would report a problem the user has no reason to care about
   * mid-task. That is the opposite of the read above, where an empty list would
   * be a claim about the hotel and so has to surface as an error.
   */
  const pickerProperty = propertyCanonicalId ?? hotelCanonicalId;

  /**
   * Refetched ON MOUNT AND ON EVERY MODAL OPEN, which is a correction.
   *
   * Fetching once on mount left the picker STALE after a sibling mutation:
   * retiring someone in the `HotelContacts` table beside this panel did not
   * invalidate this list, so they stayed selectable — and logging a call
   * against a person who has left the property is the wrong record the retire
   * rule exists to prevent. Found by driving the browser; no test could see it,
   * because a test that mounts this component never has a sibling retire
   * anyone.
   *
   * `nonce` rather than a shared store: `HotelContacts` owns its own list and
   * the two are mounted by two different parents with different payloads, so
   * lifting the state means threading it through both. Re-asking a bounded
   * endpoint at the one moment the answer is used is smaller and cannot go
   * stale by construction.
   */
  const [contactsNonce, setContactsNonce] = useState(0);
  useEffect(() => {
    if (!pickerProperty) return;
    let cancelled = false;
    apiFetch<{ contacts: SelectableContact[] }>(
      `/api/btp/hotel-contacts?hotel_canonical_id=${encodeURIComponent(pickerProperty)}`,
    )
      .then((d) => {
        if (!cancelled) setContacts(d.contacts ?? []);
      })
      .catch(() => {
        if (!cancelled) setContacts([]);
      });
    return () => {
      cancelled = true;
    };
  }, [pickerProperty, contactsNonce]);

  const rows = data?.contacts ?? [];

  /*
   * Sorted on a COPY — `data.contacts` is state, and sorting in place mutates
   * it without telling React, so the first click would reorder nothing and the
   * second would appear to work.
   *
   * The id tiebreak matters more here than it looks: several contacts logged in
   * the same minute is normal (a call and an email, entered together), and
   * without it their relative order flips between renders.
   */
  const sorted = [...rows].sort((a, b) => {
    const d = Date.parse(a.contactedAt) - Date.parse(b.contactedAt);
    const by = d !== 0 ? d : Number(a.id) - Number(b.id);
    return order === 'asc' ? by : -by;
  });

  /**
   * A row about a DIFFERENT customer's agreement, on a hotel-level log.
   *
   * This is the flag behind Stacie's example — the thing Tyler needs to notice
   * before he rings Blake. On an agreement-level log it is always false: every
   * row there is about this agreement by definition.
   */
  const otherAgreement = (c: SupplierContact) =>
    !agreementId && Boolean(c.agreement);

  /**
   * Was the property contacted by someone else, today, about something else?
   *
   * The narrowest useful version of "somebody just called them". Same DAY
   * rather than same hour, because that is the window in which a second call
   * is awkward, and by a DIFFERENT person because your own earlier call is not
   * news to you.
   */
  const contactedTodayByOthers = agreementId
    ? []
    : rows.filter(
        (c) =>
          dayKeyOf(c.contactedAt) === dayKeyOf(new Date().toISOString()) &&
          c.direction === 'outbound',
      );

  if (!query) return null;

  return (
    <div data-testid={testId}>
      {/*
       * LOG A CONTACT SITS ABOVE THE HISTORY. Stacie, 2026-09-03, twice, and
       * she named it: "that's my main ask."
       *
       * It used to be last, below the summary, the table and the truncation
       * note. The reasoning then was that a button above a list "would be
       * pushed down" once the list loaded — which optimises for the first
       * second of the panel's life over every second after it. Logging a call
       * is the ACTION of this section; the history is reference material for
       * it. The action goes where the eye lands.
       *
       * Still rendered when the READ failed, and that is deliberate: being
       * unable to show the history is no reason to stop someone recording a
       * call they just made. Losing the record is worse than showing it late.
       * It is no longer gated on `!loading` either — the old gate existed only
       * to keep it from jumping, and at the top there is nothing to jump over.
       */}
      <div style={{ marginBottom: 12 }}>
        <LogContactForm
          agreementId={agreementId}
          agreements={agreements}
          contacts={contacts}
          hotelCanonicalId={hotelCanonicalId}
          onOpen={() => setContactsNonce((n) => n + 1)}
          onSaved={() => {
            /*
             * BOTH: this panel's own history, and whatever the host derives
             * from it. The panel reload is what makes the new row appear here;
             * the host callback is what stops the Agreements row's "Last
             * contacted by / date" sitting stale until a page refresh.
             */
            load();
            onContactSaved?.();
          }}
          testId={`${testId}-add`}
        />
      </div>

      {/*
       * "SOMEBODY ALREADY RANG THEM TODAY" — above the button, because it is
       * only useful BEFORE you make the call.
       *
       * This is the point of Stacie's worked example, made into something the
       * screen says rather than something you have to read the table to
       * notice: "if Tyler calls Little America to talk about loading the
       * agreement and he sees that I just talked to Blake yesterday, he could
       * be like — hey Blake, sorry, I know Stacie called, this is something
       * different."
       *
       * Names the people, because "1 contact today" is a number and "Stacie
       * called" is the thing that changes how Tyler opens the call.
       */}
      {contactedTodayByOthers.length > 0 && (
        <p
          className="detail-note"
          data-testid={`${testId}-today`}
          style={{ margin: '0 0 12px' }}
        >
          <span className="badge badge-amber badge-no-dot">
            Contacted today
          </span>{' '}
          <span className="muted">
            {[...new Set(contactedTodayByOthers.map((c) => c.contactedBy))]
              .map((who) => who.split('@')[0])
              .join(', ')}{' '}
            already reached out to this property today
            {contactedTodayByOthers.some((c) => c.agreement)
              ? ' — check what it was about before you call.'
              : '.'}
          </span>
        </p>
      )}

      {loading && !data && (
        <p
          data-testid={`${testId}-loading`}
          style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}
        >
          Loading contact history…
        </p>
      )}

      {error && (
        <p
          data-testid={`${testId}-error`}
          style={{ fontSize: 12, color: 'var(--text-primary)', margin: 0 }}
        >
          Contact history isn&rsquo;t available right now — this is a loading or
          connection problem, not an empty log.
        </p>
      )}

      {!loading && !error && data && data.total === 0 && (
        <p
          data-testid={`${testId}-empty`}
          style={{ fontSize: 12, color: 'var(--text-secondary)', margin: 0 }}
        >
          No contact recorded here yet. That is not the same as never contacted
          — anything before this log existed was not captured.
        </p>
      )}

      {!error && data && data.last_contact && (
        <>
          {/*
           * ONLY THE AGGREGATE SURVIVES (item 69, cut on 2026-09-11).
           *
           * This summary carried five fields, and FOUR of them — when, method,
           * by, direction — were the first row of the table immediately below
           * it, rendered a second time through a second code path. On 2026-09-10
           * the two disagreed on screen: the summary read Sep 7 while the top
           * row read Sep 9. That instance was a harness bug, but the
           * duplication is what made a disagreement possible at all, and it
           * would have happened again for a different reason.
           *
           * "Awaiting reply" is the one thing here that is NOT in the table: it
           * counts outbound contacts with no inbound since, across the whole
           * history. It stays, and only when there is something outstanding —
           * a permanent "0 unanswered" is noise; a badge that appears only when
           * it means something is the thing people notice.
           */}
          {data.last_contact.awaitingReply > 0 && (
            <p
              data-testid={`${testId}-awaiting`}
              style={{ margin: '0 0 12px' }}
            >
              <span className="badge badge-amber badge-no-dot">
                {data.last_contact.awaitingReply} unanswered
              </span>
            </p>
          )}

          {/*
           * SORTABLE BY WHEN. Stacie, 2026-09-03: "what if we could just even
           * like click to sort the column."
           *
           * Sorted in the BROWSER, unlike the agreements list, and the
           * difference is not an inconsistency: this list is one parent's
           * history capped at MAX_CONTACTS server-side, so every row is
           * already here and a request would re-fetch what is in hand. The
           * agreements list is a page of an unbounded table, where a
           * browser-side sort would order only the rows you happen to have and
           * look exactly like a sort that works.
           */}
          <table className="detail-table" data-testid={`${testId}-table`}>
            <thead>
              <tr>
                <th>
                  <button
                    className="th-sort"
                    data-sorted={order}
                    data-testid={`${testId}-sort-when`}
                    onClick={() =>
                      setOrder((o) => (o === 'desc' ? 'asc' : 'desc'))
                    }
                    type="button"
                  >
                    When
                    <span aria-hidden="true" className="th-sort-glyph">
                      {order === 'asc' ? '▲' : '▼'}
                    </span>
                  </button>
                </th>
                <th>Direction</th>
                <th>Method</th>
                <th>By</th>
                {/* Only on a HOTEL-level log, where rows belong to different
                    customers. On an agreement it would repeat the parent in
                    every row. */}
                {!agreementId && <th>About</th>}
                <th>Who</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((c) => (
                <tr
                  data-other-agreement={otherAgreement(c) ? 'true' : undefined}
                  data-testid={`${testId}-row-${c.id}`}
                  key={c.id}
                >
                  <td>{fmtInstant(c.contactedAt) ?? '—'}</td>
                  <td>{label(DIRECTION_LABELS, c.direction)}</td>
                  <td>{methodsOf(c)}</td>
                  <td>{c.contactedBy}</td>
                  {!agreementId && (
                    <td>
                      {c.agreement ? (
                        <>
                          {c.agreement.customerName ??
                            c.agreement.customerId ??
                            'an agreement'}
                          {c.reason && c.reason !== 'general' && (
                            <div className="cell-sub">
                              {contactReasonLabel(c.reason)}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="cell-sub">
                          {c.reason
                            ? contactReasonLabel(c.reason)
                            : 'the hotel'}
                        </span>
                      )}
                    </td>
                  )}
                  <td>
                    {c.participants?.length
                      ? c.participants.map((p) => p.name).join(', ')
                      : '—'}
                  </td>
                  <td>{c.notes || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/*
           * SAYS SO WHEN TEST ROWS WERE WITHHELD.
           *
           * The alternative was to hide them silently, which would make this
           * log quietly disagree with the database — and someone chasing "why
           * does the journey pass but I see nothing" would have no thread to
           * pull. One line, only when the number is non-zero, in the same
           * spirit as the truncation note below it.
           */}
          {(data.test_contacts_hidden ?? 0) > 0 && (
            <p
              data-testid={`${testId}-test-hidden`}
              style={{
                fontSize: 11,
                marginTop: 8,
                color: 'var(--text-secondary)',
              }}
            >
              {data.test_contacts_hidden!.toLocaleString()} automated test
              contact
              {data.test_contacts_hidden === 1 ? '' : 's'} on this record
              {data.test_contacts_hidden === 1 ? ' is' : ' are'} not shown.
            </p>
          )}

          {/* Says so when the list is a page rather than the whole history — the
              same discipline as the CSV export's row-count note. */}
          {data.truncated && (
            <p
              data-testid={`${testId}-truncated`}
              style={{
                fontSize: 11,
                marginTop: 8,
                color: 'var(--text-primary)',
              }}
            >
              Showing the {data.contacts.length.toLocaleString()} most recent of{' '}
              {data.total.toLocaleString()} recorded contacts.
            </p>
          )}
        </>
      )}
    </div>
  );
}
